import { Divider, Tab, Tabs } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import * as React from "react";
import { FC, Fragment, useCallback, useEffect, useState } from "react";
import {
  BooleanField,
  Datagrid,
  Filter,
  FilterProps,
  Identifier,
  List,
  ListContextProvider,
  ListProps,
  ReferenceInput,
  SelectInput,
  TextField,
  TextInput,
  useGetList,
  useListContext,
} from "react-admin";
import { CustomDateField } from "../../../Components/CustomDateField";
import { CustomDatetime } from "../../../Components/CustomDatetime";
import CreatedByField from "../../../Components/ReferenceFields/CreatedBy/CreatedByField";
import CustomerField from "../../../Components/ReferenceFields/Customer/CustomerField";
import { DefaultCreatedDateSort, DefaultIsActiveFilter, DefaultPerPageSize } from "../../../Constants/Constant";
import { ActivePassiveStatus } from "../../../Enums/Active";
import { ExcelExport } from "../../../Helper/ExportExcel";
import { TabbedDatagridProps } from "../../../Models/TabbedDataGridProps";
import { SourceCustomer } from "../Customer";
import { RemovalReasonTypeExcelHeader, RemovalReasonTypeExcelName } from "./Constant/Excel";
import { SourceRemoveReasonTypes, TitleRemoveReasonTypes } from "./Constant/Source";

const RemoveReasonFilter: FC<Omit<FilterProps, "children">> = props => (
  <Filter {...props}>
    <ReferenceInput
      label="CUSTOMER"
      source="customerId"
      perPage={99999}
      reference={SourceCustomer}
      translateChoice={false}
    >
      <SelectInput optionText="customerName" optionValue="id" />
    </ReferenceInput>
    <TextInput label="Removal Reason Code" source="removeReasonTypeCode__contains" alwaysOn />
    <TextInput label="Removal Reason Name" source="removeReasonTypeName__contains" alwaysOn />
    <CustomDatetime source="createdDate__gte" label="Tarih(>=)" />
    <CustomDatetime source="createdDate__lte" label="Tarih (<=)" />
  </Filter>
);

const ExportToExcel = data => {
  ExcelExport(
    RemovalReasonTypeExcelHeader,
    data.map(v => [v.removeReasonTypeCode, v.removeReasonTypeName, v.isRequired, v.createdBy, v.createdDate]),
    RemovalReasonTypeExcelName
  );
};

const useDatagridStyles = makeStyles({
  total: { fontWeight: "bold" },
});

const useGetTotals = (filterValues: any) => {
  const { total: totalIsActive } = useGetList(
    SourceRemoveReasonTypes,
    { perPage: 100, page: 1 },
    { field: "createdDate", order: "DESC" },
    { ...filterValues, isActive__eq: true }
  );
  const { total: totalIsPassive } = useGetList(
    SourceRemoveReasonTypes,
    { perPage: 100, page: 1 },
    { field: "createdDate", order: "DESC" },
    { ...filterValues, isActive__eq: false }
  );

  return {
    active: totalIsActive,
    passive: totalIsPassive,
  };
};

const TabbedDatagrid: FC<TabbedDatagridProps> = props => {
  const listContext = useListContext();
  const { ids, filterValues, setFilters, displayedFilters } = listContext;
  const [isActive, setIsActive] = useState<Identifier[]>([] as Identifier[]);
  const [isPassive, setIsPassive] = useState<Identifier[]>([] as Identifier[]);

  const totals = useGetTotals(filterValues) as any;

  useEffect(() => {
    if (ids && ids !== filterValues.isActive__eq) {
      switch (filterValues.isActive__eq) {
        case true:
          setIsActive(ids);
          break;
        case false:
          setIsPassive(ids);
          break;
      }
    }
  }, [ids, filterValues.isActive__eq]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<{}>, value: any) => {
      setFilters && setFilters({ ...filterValues, isActive__eq: value == 1 }, displayedFilters);
    },
    [displayedFilters, filterValues, setFilters]
  );

  return (
    <Fragment>
      <Tabs
        variant="fullWidth"
        centered
        value={filterValues.isActive__eq ? 1 : 0}
        indicatorColor="primary"
        onChange={handleChange}
      >
        {ActivePassiveStatus.map(choice => (
          <Tab
            key={choice.id}
            label={totals[choice.name] ? `${choice.name} (${totals[choice.name]})` : choice.name}
            value={choice.id}
          />
        ))}
      </Tabs>
      <Divider />

      <ListContextProvider
        value={{
          ...listContext,
          ids: filterValues.isActive_eq ? isActive : isPassive,
        }}
      >
        <Datagrid {...props} optimized rowClick="edit">
          <CustomerField label="CUSTOMER" />
          <TextField source="removeReasonTypeCode" label="REMOVE REASON CODE" />
          <TextField source="removeReasonTypeName" label="REMOVE REASON NAME" />
          <BooleanField source="isRequired" label="IS REQUIRED" />
          <CreatedByField label="CREATED BY" link={false} />
          <CustomDateField source="createdDate" label="CREATED DATE" showTime />
        </Datagrid>
      </ListContextProvider>
    </Fragment>
  );
};

const RemoveReasonList: FC<ListProps> = props => (
  <List
    {...props}
    title={TitleRemoveReasonTypes.list}
    filterDefaultValues={DefaultIsActiveFilter}
    sort={DefaultCreatedDateSort}
    perPage={DefaultPerPageSize}
    exporter={ExportToExcel}
    filters={<RemoveReasonFilter />}
  >
    <TabbedDatagrid />
  </List>
);

export default RemoveReasonList;
